import {
    type ExtendedRegExpMatchArray,
    mergeAttributes,
    Node,
    nodePasteRule,
    type PasteRuleMatch,
} from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";

import { type IEntityLinkRouterHandler } from "helpers/routing/EntityLinkRouter/EntityLinkRouter.api.handler";

import { type EntityTypes } from "types/enum";

import { getPathFromBTUrl, routes } from "utilities/routes";

import {
    EntityLinkNodeView,
    type IEntityMetadata,
} from "commonComponents/btWrappers/editor/core/extensions/custom/entityLink/EntityLinkNodeView";

declare module "@tiptap/core" {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Commands<ReturnType> {
        entityLink: {
            /**
             * Convert an entity link to a regular link. This command will only work if the selection
             * is a single node and that node is an entity link.
             */
            convertEntityLinkToLink: (linkText: string, href: string) => ReturnType;
        };
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention -- `Storage` is the third-party interface in @tiptap/core; we cannot rename it.
    interface Storage {
        entityLink?: IEntityLinkStorage;
    }
}

export interface IEntityLinkOptions {
    /**
     * The handler that EntityLinkNodeView uses to get linked item data
     * If this is undefined, the extension will automatically replace the node with a regular link
     */
    handler?: IEntityLinkRouterHandler;
}

export interface IEntityLinkStorage {
    /**
     * Stores metadata for entities based on their externalId. It allows for duplicate or recreated
     * nodes to cache their metadata. Nodes with the same externalId will have the same metadata.
     */
    nodeMetadata: Map<string, IEntityMetadata>;
}

export interface IEntityLinkAttributes {
    externalId: string | undefined;
    url: string | undefined;
    entityType: number | undefined;
    id: number | undefined;
    jobId: number | undefined;
}

export interface ISPANodeMetadata {
    id: number;
    jobId: number;
    entityType: EntityTypes;
}

function getNodeTitle(metadata: IEntityMetadata, padWithSpaces: boolean = false) {
    const pad = padWithSpaces ? " " : "";
    return `${pad}${metadata.relatedEntity.name}: ${metadata.entityRecord.title}${pad}`;
}

const EntityLinkExtension = Node.create<IEntityLinkOptions>({
    name: "entityLink",
    group: "inline",
    inline: true,
    atom: true,
    selectable: true,
    draggable: true,
    priority: 51,
    marks: "",

    addCommands() {
        return {
            convertEntityLinkToLink:
                (linkText: string, href: string) =>
                ({ dispatch, can, chain, state, tr }) => {
                    const { selection } = tr;
                    const { empty, from, to } = selection;

                    const canConvert = can()
                        .chain()
                        .focus()
                        .deleteSelection()
                        .setLink({ href, target: "_blank" })
                        .insertContent(linkText)
                        .run();

                    const isSingleNode = !empty && from + 1 === to;

                    if (dispatch && canConvert && isSingleNode) {
                        const node = state.doc.nodeAt(from);

                        // If the node is an entity link node
                        if (node && node.type.name === this.name) {
                            chain()
                                .focus()
                                .deleteSelection()
                                .setLink({ href, target: "_blank" })
                                .insertContent(linkText);
                            return true;
                        }
                    }
                    return canConvert;
                },
        };
    },

    addStorage() {
        return {
            nodeMetadata: new Map<string, IEntityMetadata>(),
        } as IEntityLinkStorage;
    },

    addAttributes() {
        return {
            externalId: {
                default: undefined,
                parseHTML: (element) => element.getAttribute("externalId"),
                renderHTML: (elementAttributes) => {
                    return { externalId: elementAttributes["externalId"] };
                },
            },

            // These 4 attributes are used ONLY when pasting SPA URLs.
            // When these are set, the node view will generate a GUID with them, then
            // set each to undefined.
            // These attributes will NOT be stored on the backend.
            url: {
                default: undefined,
            },
            entityType: {
                default: undefined,
            },
            id: {
                default: undefined,
            },
            jobId: {
                default: undefined,
            },
        };
    },

    addPasteRules() {
        const createSpaUrlPasteRule = (parseFunc: (text: string) => ISPANodeMetadata | null) => {
            return nodePasteRule({
                find: (text: string) => {
                    // check if the URL is a BT URL
                    const path = getPathFromBTUrl(text);
                    if (!path) return null;

                    // check if the URL is for the correct entity
                    const data = parseFunc(path);
                    if (!data) return null;
                    return [
                        {
                            text: text,
                            data: data,
                            index: 0,
                        } as PasteRuleMatch,
                    ];
                },
                type: this.type,
                getAttributes: (match: ExtendedRegExpMatchArray) => {
                    return {
                        externalId: undefined, // EntityLinkNodeView will populate this
                        url: match[0],
                        id: match.data?.id,
                        jobId: match.data?.jobId,
                        entityType: match.data?.entityType,
                    };
                },
            });
        };

        return [
            nodePasteRule({
                find: (text) => {
                    const path = getPathFromBTUrl(text);
                    if (!path) return null;
                    const result = routes.entityLink.parsePath(path);
                    if (!result) return null;
                    return [
                        {
                            text: text,
                            data: {
                                externalId: result.externalId,
                            },
                            index: 0,
                        } as PasteRuleMatch,
                    ];
                },
                type: this.type,
                getAttributes: (match) => {
                    return { externalId: match.data?.externalId };
                },
            }),
            createSpaUrlPasteRule(routes.toDo.parseDetailsPath),
            createSpaUrlPasteRule(routes.schedule.parseDetailsPath),
            createSpaUrlPasteRule(routes.dailyLog.parseDetailsPath),
        ];
    },

    renderHTML({ node, HTMLAttributes }) {
        let tagBody = "";
        let href = "";

        // SPA Link loading state
        if (node.attrs.entityType) {
            tagBody = node.attrs.url;
            href = node.attrs.url;
        } else {
            const storage = this.editor?.storage.entityLink;
            const fullLink = routes.entityLink.getFullLink(node.attrs.externalId);
            const nodeMetadata = storage?.nodeMetadata.get(node.attrs.externalId);

            // This can get called before the storage is populated, so use the original share link if
            // that happens
            tagBody = nodeMetadata ? getNodeTitle(nodeMetadata) : fullLink;
            href = fullLink;
        }

        return [
            "a",
            mergeAttributes(HTMLAttributes, {
                "data-type": "entity-link",
                href: href,
            }),
            tagBody,
        ];
    },

    renderText({ node }) {
        // SPA Link loading state
        if (node.attrs.url) {
            return node.attrs.url;
        }

        const storage = this.editor?.storage.entityLink;
        const fullLink = routes.entityLink.getFullLink(node.attrs.externalId);
        const nodeMetadata = storage?.nodeMetadata.get(node.attrs.externalId);
        return nodeMetadata ? getNodeTitle(nodeMetadata, true) : fullLink;
    },

    parseHTML() {
        return [{ tag: "a[data-type=entity-link]", priority: 51 }];
    },

    addNodeView() {
        return ReactNodeViewRenderer(EntityLinkNodeView, {
            as: "span",
        });
    },
});

export default EntityLinkExtension;
